<template>
  <div class="admin-container">
    <div class="h-screen flex flex-col w-[90%] mx-auto pt-10 pb-7">
      <div class="flex-shrink-0">
        <div class="px-6">
          <BackButton class="mb-6" />
          <h4 class="authHeader">Admin Dashboard</h4>
          <p class="text-top">Generate deploy codes, deploy docks and manage customer devices.</p>
        </div>

        <TabNavigation class="mt-4" :tabs="tabs" v-model="activeTab" />
      </div>

      <div class="flex-1 overflow-y-auto mt-8 px-6">
        <!-- Deploy Code Generator Section -->
        <div v-show="activeTab === 'generator'" class="generator-section">
          <div class="grid-layout">
            <!-- Left Column: Input Form -->
            <div class="form-card">
              <div class="card-header">
                <div class="header-icon">
                  <Icon name="settings" :size="20" color="#1b92a6" />
                </div>
                <div>
                  <h3 class="card-title">Deployment Configuration</h3>
                  <p class="card-subtitle">Enter the deployment parameters below</p>
                </div>
              </div>

              <div class="form-grid">
                <NumericInput
                  type="text"
                  label="Machine"
                  :value="deployData.machine"
                  placeholder="Enter machine identifier"
                  @update:value="(v: string) => (deployData.machine = v)"
                />

                <NumericInput
                  type="text"
                  label="MQTT Host"
                  :value="deployData.mqttHost"
                  placeholder="e.g., mqtt.example.com"
                  @update:value="(v: string) => (deployData.mqttHost = v)"
                />

                <NumericInput
                  type="text"
                  label="MQTT Port"
                  :value="deployData.mqttPort"
                  placeholder="e.g., 1883"
                  @update:value="(v: string) => (deployData.mqttPort = v)"
                />

                <NumericInput
                  type="text"
                  label="Gateway SN"
                  :value="deployData.gatewaySN"
                  placeholder="Enter gateway serial number"
                  @update:value="(v: string) => (deployData.gatewaySN = v)"
                />
                <ToggleSwitch
                  label="Run Docker Compose"
                  v-model="deployData.composeup"
                  :showLabel="true"
                  labelPosition="right"
                />
              </div>

              <div class="form-actions">
                <button
                  class="generate-btn"
                  @click="generateCode"
                  :disabled="!isFormValid || isGenerating"
                >
                  <Loader v-if="isGenerating" />
                  <span v-else>Generate Deploy Code</span>
                </button>
                <button class="clear-btn" @click="clearForm" :disabled="isGenerating">
                  <span>Clear</span>
                </button>
              </div>
            </div>
            <div class="output-card">
              <!-- History Section -->
              <div v-if="settings.codeHistory?.length > 0" class="history-section">
                <div class="history-header">
                  <h4 class="history-title">Recent Codes</h4>
                  <button class="clear-history-btn" @click="clearHistory">Clear All</button>
                </div>
                <div class="history-list max-h-[600px] overflow-y-auto">
                  <div
                    v-for="(item, index) in settings.codeHistory"
                    :key="index"
                    class="history-item"
                  >
                    <div class="history-info">
                      <p class="history-machine">{{ item.machine || 'Unknown' }}</p>
                      <p class="history-time">{{ item.generatedAt }}</p>
                    </div>
                    <div>
                      <copy :size="18" :value="item.code" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-show="activeTab === 'deploy-dock'">
          <AdminDeployDock />
        </div>
        <div v-show="activeTab === 'rc-deployment'">
          <importConfigModal />
        </div>
        <div v-show="activeTab === 'device-fields'">
          <AdminDeviceFields />
        </div>
        <div v-show="activeTab === 'device-transfer'">
          <AdminDeviceTransfer />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { appStore } from '@/store/app'
import { ref, computed, reactive } from 'vue'
import BackButton from '@/components/BackButton.vue'
import TabNavigation from '@/components/BaseComponents/TabNavigation.vue'
import NumericInput from '@/components/Inputs/NumericInput.vue'
import ToggleSwitch from '@/components/BaseComponents/ToggleSwitch.vue'
import Icon from '@/components/Icon.vue'
import { encrypt } from '@/utils/decrypt'
import type { deployData } from '@/types/devices.types'
import copy from '@/components/MusterPoint/copy.vue'
import { uuid } from '@/helpers'
import Loader from '@/components/ui/Loader.vue'
import useUserSettings from '@/composables/useUserSettings'
import log from '@/utils/useLogger'
import importConfigModal from '@/components/importConfigModal.vue'
import AdminDeviceTransfer from '@/components/admin/AdminDeviceTransfer.vue'
import AdminDeviceFields from '@/components/admin/AdminDeviceFields.vue'
import AdminDeployDock from '@/components/admin/AdminDeployDock.vue'

const activeTab = ref('generator')
const isGenerating = ref(false)
const { addNotifications } = appStore()
const { settings, saveSettings } = useUserSettings()

const deployData = reactive<deployData>({
  machine: '',
  mqttHost: '',
  mqttPort: '',
  composeup: false,
  gatewaySN: '',
})

const isFormValid = computed(() => {
  return (
    deployData.machine.trim() !== '' &&
    deployData.mqttHost.trim() !== '' &&
    deployData.mqttPort.trim() !== '' &&
    deployData.gatewaySN.trim() !== ''
  )
})
const tabs = [
  {
    name: 'Deploy Code Generator',
    value: 'generator',
  },
  {
    name: 'Deploy Dock',
    value: 'deploy-dock',
  },
  {
    name: 'Rc Deployment',
    value: 'rc-deployment',
  },
  {
    name: 'Device Fields',
    value: 'device-fields',
  },
  {
    name: 'Device Transfer',
    value: 'device-transfer',
  },
]
const formatDate = (date: Date): string => {
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const generateCode = async () => {
  if (!isFormValid.value) return

  isGenerating.value = true

  try {
    const dataToEncrypt = JSON.stringify({
      machine: deployData.machine,
      mqttHost: deployData.mqttHost,
      mqttPort: deployData.mqttPort,
      composeup: deployData.composeup,
      gatewaySN: deployData.gatewaySN,
    })

    const encrypted = await encrypt(dataToEncrypt)

    if (!encrypted) {
      throw new Error('Failed to generate encrypted code')
    }
    saveSettings({
      codeHistory: [
        {
          code: encrypted,
          machine: deployData.machine,
          generatedAt: formatDate(new Date()),
        },
        ...(settings.value.codeHistory || []),
      ],
    })
    clearForm()
  } catch (err: any) {
    log.error('Failed to generate deploy code', err)
    addNotifications({
      id: uuid(10),
      notification: err.message || 'Failed to generate deploy code',
      type: 'error',
    })
  } finally {
    isGenerating.value = false
  }
}

const clearForm = () => {
  deployData.machine = ''
  deployData.mqttHost = ''
  deployData.mqttPort = ''
  deployData.composeup = false
  deployData.gatewaySN = ''
}

const clearHistory = () => {
  saveSettings({
    codeHistory: [],
  })
}
</script>

<style scoped>
.admin-container {
  background-color: var(--idi-shade1-100);
  width: 100%;
  min-height: 100vh;
}

.generator-section {
  padding-bottom: 2rem;
}

.grid-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

@media (max-width: 1200px) {
  .grid-layout {
    grid-template-columns: 1fr;
  }
}

/* Card Styles */
.form-card,
.output-card {
  background: linear-gradient(145deg, var(--idi-shade0-100) 0%, rgba(11, 14, 18, 0.95) 100%);
  border: 1px solid var(--idi-shade2-50);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.header-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(27, 146, 166, 0.15) 0%, rgba(27, 146, 166, 0.05) 100%);
  border: 1px solid rgba(27, 146, 166, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-title {
  color: #e4e4e4;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
}

.card-subtitle {
  color: #95989c;
  font-size: 13px;
  font-weight: 400;
}

/* Form Styles */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.generate-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, var(--idi-green) 0%, var(--idi-green-s1) 100%);
  color: #fff;
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
}

.generate-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(27, 146, 166, 0.3);
}

.generate-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--idi-shade3-50);
  color: #95989c;
  padding: 14px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
}

.clear-btn:hover:not(:disabled) {
  background: var(--idi-shade2-50);
  border-color: var(--idi-shade4-50);
}

.clear-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* History Section */
.history-section {
  margin-top: 8px;
  padding-top: 20px;
}

.history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.history-title {
  color: #95989c;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.clear-history-btn {
  background: transparent;
  border: none;
  color: #606366;
  font-size: 12px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.clear-history-btn:hover {
  color: #fa5353;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: var(--idi-shade1-50);
  border: 1px solid var(--idi-shade2-50);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.history-item:hover {
  background: var(--idi-shade2-50);
  border-color: var(--idi-shade3-50);
}

.history-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.history-machine {
  color: #c2c3c4;
  font-size: 13px;
  font-weight: 500;
}

.history-time {
  color: #606366;
  font-size: 11px;
}
</style>
